import React, { useEffect, useRef, useState } from "react";
import { load, save } from "@/helpers/local-storage";
import LexArxLogo from "@/assets/lexarx-logo.svg";
import "./lexarx-banner.css";

/**
 * Rotating LexArx house ad for the top of the portal.
 *
 * Copy is verbatim client language: every eyebrow and the "Don't overpay" /
 * "We aggressively challenge" lines come from the LexArx ad creatives, the
 * remaining four sentences from lex-arx.com/what-we-do.
 */
const SLIDES = [
  {
    eyebrow: "Patent Litigation Defense",
    line: "Don’t overpay for “standard” technology and weak infringement claims."
  },
  {
    eyebrow: "Demand Letter Responses",
    line: "We are your go-to counsel when your patent infringement matter starts with a demand letter."
  },
  {
    eyebrow: "NPE Litigation",
    line: "We aggressively challenge bad assertions and patents, neutralizing NPE and SEP threats."
  },
  {
    eyebrow: "Ex Parte Reexaminations",
    line: "An early and strong invalidity challenge is often the best response to a demand letter or district court complaint."
  },
  {
    eyebrow: "Litigation Threat Advising",
    line: "We analyze clients’ open source software use and help them understand what potential litigation threats they may face."
  },
  {
    eyebrow: "SEP / FRAND Licensing",
    line: "Where resolution is the goal, we offer the resources and knowledge to achieve successful licensing outcomes."
  }
];

const DWELL = 6000; // how long each message holds, ms
const HIDE_MS = 7 * 24 * 60 * 60 * 1000; // dismissing hides the ad for one week
const COLLAPSE_MS = 420; // the .is-closing height transition (360ms) plus slack
const STORAGE_KEY = "lexarx.ad.hiddenUntil";
const HREF = "https://lex-arx.com";

const LexArxBanner = () => {
  // Read the dismissal before the first paint, so a hidden ad never flashes.
  const [hidden, setHidden] = useState(() => Date.now() < load(STORAGE_KEY));
  const [index, setIndex] = useState(0);
  const [closing, setClosing] = useState(false);
  const adRef = useRef(null);
  const barRef = useRef(null);
  const animRef = useRef(null);
  const heldRef = useRef(false); // hovered or holding keyboard focus

  // The progress hairline is the timer: when it finishes, the copy advances.
  useEffect(() => {
    const bar = barRef.current;
    if (hidden || closing || !bar || !bar.animate) return undefined;
    const anim = bar.animate([{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }], {
      duration: DWELL,
      easing: "linear",
      fill: "forwards"
    });
    anim.onfinish = () => setIndex(current => (current + 1) % SLIDES.length);
    animRef.current = anim;
    if (heldRef.current || document.hidden) anim.pause();
    const onVisibilityChange = () => {
      if (document.hidden) anim.pause();
      else if (!heldRef.current) anim.play();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      anim.cancel();
      animRef.current = null;
    };
  }, [index, hidden, closing]);

  // Publish the banner's height so the app shell below can drop its own floor
  // by that much (see lexarx-banner.css) instead of overflowing the viewport.
  useEffect(() => {
    const ad = adRef.current;
    if (hidden || closing || !ad || !window.ResizeObserver) return undefined;
    let published = null;
    const observer = new ResizeObserver(([entry]) => {
      const height = Math.round(entry.contentRect.height);
      if (height === published) return; // a repeated value would recalc the whole document for nothing
      published = height;
      document.documentElement.style.setProperty("--lexarx-ad-h", `${height}px`);
    });
    observer.observe(ad);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--lexarx-ad-h");
    };
  }, [hidden, closing]);

  // Collapse, then unmount. The height start value is frozen in the click
  // handler below, so this only has to animate towards zero.
  useEffect(() => {
    if (!closing) return undefined;
    if (adRef.current) adRef.current.style.height = "0px";
    const timer = setTimeout(() => setHidden(true), COLLAPSE_MS);
    return () => clearTimeout(timer);
  }, [closing]);

  const hold = state => {
    heldRef.current = state;
    const anim = animRef.current;
    if (!anim) return;
    if (state) anim.pause();
    else anim.play();
  };

  const dismiss = () => {
    save(STORAGE_KEY, Date.now() + HIDE_MS);
    const ad = adRef.current;
    const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!ad || reduceMotion) {
      setHidden(true);
      return;
    }
    ad.style.height = `${ad.offsetHeight}px`;
    ad.getBoundingClientRect(); // flush, so the transition has a start value
    setClosing(true);
  };

  if (hidden) return null;

  return (
    <aside
      ref={adRef}
      className={closing ? "lexarx-ad is-closing" : "lexarx-ad"}
      aria-label="Advertisement: Lex Arx patent litigation defense"
      onMouseEnter={() => hold(true)}
      onMouseLeave={() => hold(false)}
      onFocus={() => hold(true)}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) hold(false);
      }}
    >
      <div className="lexarx-ad__inner">
        <svg className="lexarx-ad__rings" viewBox="0 0 200 100" preserveAspectRatio="xMaxYMid slice" aria-hidden="true">
          <g fill="none" stroke="#7f8ea8" strokeOpacity=".22" strokeWidth=".8">
            <circle cx="176" cy="50" r="56" />
            <circle cx="176" cy="50" r="38.5" />
          </g>
        </svg>

        <a className="lexarx-ad__mark" href={HREF} target="_blank" rel="noopener noreferrer nofollow" aria-label="Lex Arx">
          <LexArxLogo className="lexarx-ad__logo" aria-hidden="true" />
          <span className="lexarx-ad__rule" />
          <span className="lexarx-ad__strap">Changing the patent game</span>
        </a>

        {/* The whole stage is the link; its accessible name is the visible
            slide, since the others are aria-hidden. */}
        <a className="lexarx-ad__stage" href={HREF} target="_blank" rel="noopener noreferrer nofollow">
          {SLIDES.map((slide, position) => (
            <p
              key={slide.eyebrow}
              className={position === index ? "lexarx-ad__slide is-on" : "lexarx-ad__slide"}
              aria-hidden={position !== index}
            >
              <span className="lexarx-ad__eyebrow">{slide.eyebrow}</span>
              <span className="lexarx-ad__line">{slide.line}</span>
            </p>
          ))}
        </a>

        <div className="lexarx-ad__rail">
          <a className="lexarx-ad__cta" href={HREF} target="_blank" rel="noopener noreferrer nofollow">
            <span className="lexarx-ad__cta-label">lex-arx.com</span>
            <svg width="15" height="9" viewBox="0 0 15 9" fill="none" aria-hidden="true">
              <path d="M0 4.5h13M9.6 1l3.4 3.5L9.6 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
          <div className="lexarx-ad__controls">
            <div className="lexarx-ad__dots" aria-label="Choose message">
              {SLIDES.map((slide, position) => (
                <button
                  key={slide.eyebrow}
                  className="lexarx-ad__dot"
                  type="button"
                  aria-current={position === index ? "true" : undefined}
                  aria-label={`Show message ${position + 1}: ${slide.eyebrow}`}
                  onClick={() => setIndex(position)}
                />
              ))}
            </div>
            <button
              className="lexarx-ad__close"
              type="button"
              aria-label="Hide this ad for one week"
              title="Hide this ad for one week"
              onClick={dismiss}
            >
              <svg width="11" height="11" viewBox="0 0 11 11" aria-hidden="true">
                <path d="M1 1l9 9M10 1l-9 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="lexarx-ad__progress" aria-hidden="true">
        <i ref={barRef} />
      </div>
    </aside>
  );
};

export default LexArxBanner;
